// See @store/AppPhaseStore.ts for the store loading order.
// Manages the user's authentication session.
// Call authenticate() to fetch/create a session, resolve the active organization,
// and populate user + token. Depends only on EnvStore (to skip auth in local-offline mode).

import { create } from 'zustand';
import type { AuthSessionResponse, User } from '@visualizer/types';
import { ApiRoutes } from './ApiRoutes';

class AuthenticationError extends Error {
    public readonly statusCode: number;

    constructor(message: string, statusCode: number) {
        super(message);
        this.name = 'AuthenticationError';
        this.statusCode = statusCode;
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AuthenticationError);
        }
    }
}

export type SessionStatus = 'idle' | 'authenticated' | 'unauthenticated' | 'error';

type BillingSubscriptionResponse = {
    id?: string;
    status?: string | null;
    entitlements?: Array<{
        lookupKey?: string | null;
    }> | null;
};

type ActiveMemberRoleResponse = {
    role?: string | null;
};

type SessionStore = {
    status: SessionStatus;
    user: User;
    token: string;
    organizationId: string;
    hasObservationReports: boolean;
    hasVideoAndNumericalExports: boolean;
    error: string | null;
    errorCode: number | null;
};

type SessionActions = {
    authenticate: () => Promise<void>;
};

const isLocalhost = () => ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);

const fetchSession = async (): Promise<AuthSessionResponse | null> => {
    const response = await fetch(`/api/auth/get-session`, {
        credentials: 'include',
    });

    if (response.status === 401) {
        return null;
    }

    if (!response.ok) {
        throw new AuthenticationError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }

    const data = (await response.json()) as AuthSessionResponse | null;
    if (!data?.session || !data?.user) {
        return null;
    }

    return data;
};

const signInWithLocalCredentials = async (): Promise<AuthSessionResponse | null> => {
    const email = import.meta.env.VITE_LOGIN_EMAIL?.trim();
    const password = import.meta.env.VITE_LOGIN_PASS?.trim();

    if (!email || !password) {
        throw new AuthenticationError('LOCAL_LOGIN_MISSING_CREDENTIALS', 400);
    }

    const response = await fetch(`/api/auth/sign-in/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, rememberMe: true }),
    });

    if (!response.ok) {
        throw new AuthenticationError('LOCAL_LOGIN_FAILED', 400);
    }

    return fetchSession();
};

const getAuthHeaders = (token?: string): HeadersInit => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
};

export const listOrganizations = async (token?: string): Promise<{ id: string; name?: string; slug?: string }[]> => {
    const response = await fetch(`/api/auth/organization/list`, {
        credentials: 'include',
        headers: getAuthHeaders(token),
    });

    if (!response.ok) {
        throw new AuthenticationError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }

    return (await response.json()) as { id: string; name?: string; slug?: string }[];
};

export const setActiveOrganization = async (organizationId: string, token?: string): Promise<void> => {
    const response = await fetch(`/api/auth/organization/set-active`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ organizationId }),
    });

    if (!response.ok) {
        throw new AuthenticationError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }
};

const resolveActiveOrganizationId = async (sessionResponse: AuthSessionResponse): Promise<string | null> => {
    const sessionOrganizationId = sessionResponse.session?.activeOrganizationId ?? null;
    if (sessionOrganizationId) {
        return sessionOrganizationId;
    }

    const token = sessionResponse.session?.token;
    if (!token) {
        return null;
    }

    const organizations = await listOrganizations(token);
    const fallbackOrganization = organizations[0];
    if (!fallbackOrganization?.id) {
        return null;
    }

    await setActiveOrganization(fallbackOrganization.id, token);
    const refreshedSession = await fetchSession();

    return refreshedSession?.session?.activeOrganizationId ?? fallbackOrganization.id;
};

const fetchBillingSubscription = async (token?: string): Promise<BillingSubscriptionResponse | null> => {
    const response = await fetch(`/api/billing/subscription`, {
        credentials: 'include',
        headers: getAuthHeaders(token),
    });

    if (response.status === 404) {
        return null;
    }

    if (!response.ok) {
        throw new AuthenticationError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }

    return (await response.json()) as BillingSubscriptionResponse | null;
};

const fetchActiveMemberRole = async (token?: string): Promise<string | null> => {
    const response = await fetch(`/api/auth/organization/get-active-member-role`, {
        credentials: 'include',
        headers: getAuthHeaders(token),
    });

    if (!response.ok) {
        throw new AuthenticationError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }

    const data = (await response.json()) as ActiveMemberRoleResponse | null;
    return data?.role ?? null;
};

const hasEntitlement = (subscription: BillingSubscriptionResponse | null, lookupKey: string): boolean =>
    (subscription?.entitlements ?? []).some((entitlement) => entitlement?.lookupKey === lookupKey);

export const useSessionStore = create<SessionStore & SessionActions>((set) => ({
    status: 'idle',
    user: JSON.parse((typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null) || '{}'),
    token: '',
    organizationId: '',
    hasObservationReports: true,
    hasVideoAndNumericalExports: true,
    error: null,
    errorCode: null,

    authenticate: async () => {
        if (!ApiRoutes.canUseApi()) {
            set({ status: 'authenticated' });
            return;
        }

        try {
            let sessionResponse = await fetchSession();
            if (!sessionResponse && isLocalhost()) {
                sessionResponse = await signInWithLocalCredentials();
            }

            if (!sessionResponse?.session || !sessionResponse.user) {
                throw new AuthenticationError('NOT_AUTHENTICATED', 401);
            }

            const resolvedOrganizationId = await resolveActiveOrganizationId(sessionResponse);
            if (!resolvedOrganizationId) {
                throw new AuthenticationError('WRONG_ORGANIZATION', 403);
            }

            const [subscription, activeMemberRole] = await Promise.all([
                fetchBillingSubscription(sessionResponse.session.token),
                fetchActiveMemberRole(sessionResponse.session.token),
            ]);

            if (!subscription || subscription.status !== 'active') {
                throw new AuthenticationError(activeMemberRole === 'owner' ? 'NO_ACTIVE_PLAN_OWNER' : 'NO_ACTIVE_PLAN_MEMBER', 402);
            }

            const hasObservationReports = hasEntitlement(subscription, 'observation-reports');
            const hasVideoAndNumericalExports = hasEntitlement(subscription, 'video-and-numerical-exports');

            const currentUser: User = JSON.parse(localStorage.getItem('user') || '{}');
            const user: User = {
                ...currentUser,
                id: sessionResponse.user.id,
                name: sessionResponse.user.name,
                email: sessionResponse.user.email,
                role: activeMemberRole ?? sessionResponse.user.role,
                account_id: resolvedOrganizationId,
                token: sessionResponse.session.token,
                user_id: sessionResponse.user.id,
            };

            set({
                status: 'authenticated',
                user,
                token: sessionResponse.session.token,
                organizationId: resolvedOrganizationId,
                hasObservationReports,
                hasVideoAndNumericalExports,
                error: null,
                errorCode: null,
            });
        } catch (e) {
            if (e instanceof AuthenticationError) {
                set({
                    status: e.statusCode === 401 || e.statusCode === 400 ? 'unauthenticated' : 'error',
                    hasObservationReports: true,
                    hasVideoAndNumericalExports: true,
                    error: e.message,
                    errorCode: e.statusCode,
                });
            } else {
                set({
                    status: 'error',
                    hasObservationReports: true,
                    hasVideoAndNumericalExports: true,
                    error: 'Unknown authentication error',
                    errorCode: 500,
                });
            }
        }
    },
}));

export const getSessionStore = () => useSessionStore.getState();
